import {
  accrualRelevant,
  kmdBackcastSummary,
  kmdDeclarationMonthOf,
  kmdVariance,
  KMD_MIRROR_SOURCES,
  type KmdBackcastRow,
} from '@progear/finance-core';
import type { CashFlowEntry } from '@/lib/cashFlow';
import type { KmdMonthStatView } from './calendar/HoverCards';
import { formatMonthEt } from '@/lib/forecast';
import type { LedgerScope } from './Ledger';

/**
 * The KMD declaration drill — SHARED by both lenses (L14): the table lens's
 * "KMD — käibemaks" INFO row and the KMD entry's enriched detail (reachable
 * from the calendar chip) both open the register through THIS builder, so the
 * drill exists once, never per-lens.
 *
 * The scope lists the CONTRIBUTING documents of a declaration month: the
 * previous month's VAT-bearing mirror rows — exactly the filter the API
 * materializer summed (`KMD_MIRROR_SOURCES` + `accrualRelevant` + nonzero VAT
 * + `documentDate` in the accrual month), so the listed rows are the ones the
 * figure was computed from (master rule: one truth). It never sums money (L9)
 * — the register does its own signed totals over the matched rows.
 */

/** The unique, sorted accrual months of a set of materialized KMD entries. */
export function kmdDeclarationMonths(entries: readonly CashFlowEntry[]): string[] {
  const months = new Set<string>();
  for (const e of entries) {
    const month = kmdDeclarationMonthOf(e.externalId);
    if (month !== null) months.add(month);
  }
  return [...months].sort();
}

/**
 * Build the register scope for one or more declaration months. With no months
 * (a column with no KMD deadline) the scope matches nothing — the register
 * opens empty under an honest label instead of falling back to "all documents".
 */
export function kmdDeclarationScope(months: readonly string[], suffix: string): LedgerScope {
  const monthPart =
    months.length === 0 ? 'deklaratsioonita' : `deklaratsioon ${months.map(formatMonthEt).join(' + ')}`;
  return {
    label: `KMD — käibemaks · ${monthPart} · ${suffix}`,
    match: (row) =>
      months.length > 0 &&
      (KMD_MIRROR_SOURCES as readonly string[]).includes(row.e.source) &&
      accrualRelevant(row.e) &&
      row.e.money.vat !== 0 &&
      row.e.documentDate !== null &&
      months.includes(row.e.documentDate.slice(0, 7)),
  };
}

/* ------------------------------------------------- past-month backcast */

/**
 * The first accrual month the SmartAccounts/Erply mirror actually covers.
 * December 2025 has no documents to back a forecast, so
 * `kmdPastDeclarationMonths` drops it rather than showing a fake €0. The table
 * makes a separate, deliberate exception for an operator-recorded actual.
 */
export const KMD_BACKCAST_EARLIEST_MONTH = '2026-01';

/** One past declaration month's backcast figure + the operator-marked actual. */
export interface KmdMonthStat {
  /** Accrual month, `YYYY-MM`. */
  month: string;
  /** finance-core `kmdBackcastSummary(...).netPayable` — what the calendar WOULD have carried. */
  forecastCents: number;
  /** The declared figure the operator marked, or null when not yet marked. */
  actualCents: number | null;
}

/**
 * Compute the backcast stats for a set of PAST declaration months from the
 * shared entry stream. The row filter + fold live in finance-core
 * (`kmdBackcastSummary` — the IDENTICAL predicate the declaration drill
 * lists); this only reshapes entries to the core's row type and pairs each
 * month with its marked actual (master rule: one truth per number).
 */
export function kmdBackcastStats(
  entries: readonly CashFlowEntry[],
  months: readonly string[],
  actuals: Readonly<Record<string, number>>,
): KmdMonthStat[] {
  const rows: KmdBackcastRow[] = [];
  for (const e of entries) {
    if (e.documentDate === null) continue;
    rows.push({ kind: e.kind, money: e.money, documentDate: e.documentDate, source: e.source, docType: e.docType });
  }
  return months.map((month) => ({
    month,
    forecastCents: kmdBackcastSummary(rows, month).netPayable,
    actualCents: actuals[month] ?? null,
  }));
}

/**
 * The €-figure a column cell SHOWS for its past months: the marked actual
 * when present, else the backcast — an integer-cents sum of already-derived
 * figures (allowed by L9; no VAT math happens here).
 */
export function kmdBackcastShownTotal(stats: readonly KmdMonthStat[]): number {
  return stats.reduce((sum, s) => sum + (s.actualCents ?? s.forecastCents), 0);
}

/**
 * Reshape the stats into the hover card's view model: the Estonian month
 * label + the finance-core variance (`kmdVariance` — € and %, one truth) for
 * each MARKED month. Unmarked months carry `variance: null` (the card shows
 * the "märkimata" prompt instead of a number).
 */
export function kmdMonthStatViews(stats: readonly KmdMonthStat[]): KmdMonthStatView[] {
  return stats.map((s) => ({
    month: s.month,
    monthLabel: formatMonthEt(s.month),
    forecastCents: s.forecastCents,
    actualCents: s.actualCents,
    variance: s.actualCents === null ? null : kmdVariance(s.forecastCents, s.actualCents),
  }));
}
